/**
 * FolderService - Handles folder operations via the File Metadata API
 * 
 * Uses DynamoDB for metadata, enabling O(1) rename/move operations.
 * Folders are first-class entities with their own IDs, not just S3 key prefixes.
 * 
 * @module FolderService
 */

import * as fileMetadataApi from './fileMetadataApi';

/**
 * Progress callback type for tracking operation progress
 * @callback ProgressCallback
 * @param {Object} progress - Progress information
 * @param {number} progress.completed - Number of completed items
 * @param {number} progress.total - Total number of items
 * @param {string} [progress.currentItem] - Current item being processed
 */

/**
 * Create a new folder
 * 
 * @param {string} name - The folder name
 * @param {string} [parentFolderId] - Parent folder ID (defaults to ROOT)
 * @returns {Promise<Object>} The created folder item
 * @throws {Error} If the create operation fails
 */
export const createFolder = async (name, parentFolderId) => {
  if (!name) {
    throw new Error('Folder name is required');
  }

  try {
    return await fileMetadataApi.createItem({
      name,
      type: 'folder',
      parentFolderId: parentFolderId || 'ROOT'
    });
  } catch (error) {
    console.error('Error creating folder:', error);
    throw new Error(`Failed to create folder "${name}": ${error.message}`);
  }
};

/**
 * List contents of a folder (files and immediate subfolders)
 * 
 * @param {string} [folderId] - The folder ID to list (defaults to ROOT)
 * @returns {Promise<{ files: Array, folders: Array }>}
 * @throws {Error} If the list operation fails
 */
export const listContents = async (folderId) => {
  try {
    const { items } = await fileMetadataApi.listItems(folderId || 'ROOT');
    
    // Separate files and folders
    const files = [];
    const folders = [];
    
    for (const item of items) {
      if (item.type === 'folder') {
        folders.push({
          id: item.id,
          name: item.name,
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date()
        });
      } else {
        files.push({
          id: item.id,
          name: item.name,
          size: item.size || 0,
          mimeType: item.mimeType,
          status: item.status,
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date()
        });
      }
    }
    
    // Sort by name
    files.sort((a, b) => a.name.localeCompare(b.name));
    folders.sort((a, b) => a.name.localeCompare(b.name));
    
    return { files, folders };
  } catch (error) {
    console.error('Error listing folder contents:', error);
    throw new Error(`Failed to list folder contents: ${error.message}`);
  }
};

/**
 * List all items recursively under a folder
 * Note: This is less efficient than the old S3 prefix listing.
 * Consider using pagination for large folders.
 * 
 * @param {string} [folderId] - The folder ID to list (defaults to ROOT)
 * @returns {Promise<Array>} Array of all items under the folder
 * @throws {Error} If the list operation fails
 */
export const listAllRecursive = async (folderId) => {
  const allItems = [];
  
  const listRecursively = async (parentId) => {
    const { items } = await fileMetadataApi.listItems(parentId);
    
    for (const item of items) {
      allItems.push(item);
      
      // Recursively list subfolders
      if (item.type === 'folder') {
        await listRecursively(item.id);
      }
    }
  };
  
  try {
    await listRecursively(folderId || 'ROOT');
    return allItems;
  } catch (error) {
    console.error('Error listing folder contents recursively:', error);
    throw new Error(`Failed to list folder contents: ${error.message}`);
  }
};

/**
 * Delete a folder
 * 
 * Note: The API will reject deletion of non-empty folders.
 * Use deleteFolder with recursive=true to delete folder and all contents.
 * 
 * @param {string} id - The folder ID to delete
 * @param {ProgressCallback} [onProgress] - Optional progress callback (for recursive delete)
 * @param {boolean} [recursive=false] - Whether to delete contents recursively
 * @returns {Promise<void>}
 * @throws {Error} If the delete operation fails
 */
export const deleteFolder = async (id, onProgress, recursive = false) => {
  if (!id) {
    throw new Error('Folder ID is required');
  }

  try {
    if (recursive) {
      // Get all items in the folder recursively
      const allItems = await listAllRecursive(id);
      const total = allItems.length + 1; // +1 for the folder itself
      
      // Delete items in reverse order (deepest first)
      // Sort by depth (count of parent folders)
      const itemsWithDepth = allItems.map(item => ({
        ...item,
        depth: countParentDepth(item, allItems)
      }));
      itemsWithDepth.sort((a, b) => b.depth - a.depth);
      
      // Delete each item
      for (let i = 0; i < itemsWithDepth.length; i++) {
        const item = itemsWithDepth[i];
        
        if (onProgress) {
          onProgress({
            completed: i,
            total,
            currentItem: item.name
          });
        }
        
        await fileMetadataApi.deleteItem(item.id);
      }
      
      // Delete the folder itself
      if (onProgress) {
        onProgress({
          completed: allItems.length,
          total,
          currentItem: 'folder'
        });
      }
      
      await fileMetadataApi.deleteItem(id);
      
      if (onProgress) {
        onProgress({
          completed: total,
          total,
          currentItem: null
        });
      }
    } else {
      // Simple delete - will fail if folder is not empty
      await fileMetadataApi.deleteItem(id);
    }
  } catch (error) {
    // Check for "folder not empty" error
    if (error.code === 'FOLDER_NOT_EMPTY') {
      throw new Error('Cannot delete folder: folder is not empty. Use recursive delete to remove all contents.');
    }
    console.error('Error deleting folder:', error);
    throw new Error(`Failed to delete folder: ${error.message}`);
  }
};

/**
 * Helper to count parent depth for sorting
 */
const countParentDepth = (item, allItems) => {
  let depth = 0;
  let currentParentId = item.parentFolderId;
  
  while (currentParentId && currentParentId !== 'ROOT') {
    const parent = allItems.find(i => i.id === currentParentId);
    if (parent) {
      depth++;
      currentParentId = parent.parentFolderId;
    } else {
      break;
    }
  }
  
  return depth;
};

/**
 * Rename a folder (O(1) operation - only updates DynamoDB metadata)
 * 
 * @param {string} id - The folder ID
 * @param {string} newName - The new folder name
 * @param {ProgressCallback} [onProgress] - Optional progress callback (unused, kept for interface compatibility)
 * @returns {Promise<Object>} The updated folder item
 * @throws {Error} If the rename operation fails
 */
export const renameFolder = async (id, newName, onProgress) => {
  if (!id) {
    throw new Error('Folder ID is required');
  }

  if (!newName) {
    throw new Error('New name is required');
  }

  try {
    const result = await fileMetadataApi.updateItem(id, { name: newName });
    
    // Call progress callback for interface compatibility
    if (onProgress) {
      onProgress({ completed: 1, total: 1, currentItem: null });
    }
    
    return result;
  } catch (error) {
    console.error('Error renaming folder:', error);
    throw new Error(`Failed to rename folder: ${error.message}`);
  }
};

/**
 * Move a folder to a different parent folder (O(1) operation)
 * 
 * @param {string} id - The folder ID
 * @param {string} destinationFolderId - The destination parent folder ID
 * @returns {Promise<Object>} The updated folder item
 * @throws {Error} If the move operation fails
 */
export const moveFolder = async (id, destinationFolderId) => {
  if (!id) {
    throw new Error('Folder ID is required');
  }

  try {
    return await fileMetadataApi.updateItem(id, { 
      parentFolderId: destinationFolderId || 'ROOT' 
    });
  } catch (error) {
    // Check for circular reference error
    if (error.code === 'CIRCULAR_REFERENCE') {
      throw new Error('Cannot move folder into itself or its descendants');
    }
    console.error('Error moving folder:', error);
    throw new Error(`Failed to move folder: ${error.message}`);
  }
};

// Export as a service object for interface compatibility
const FolderService = {
  create: createFolder,
  delete: deleteFolder,
  rename: renameFolder,
  move: moveFolder,
  listContents,
  listAllRecursive
};

export default FolderService;
